import {
  BriefcaseBusiness,
  CheckCircle2,
  CreditCard,
  LayoutDashboard,
  LogOut,
  Menu,
  Plug,
  Users,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/providers/auth-provider';

function isNavigationItemActive(pathname: string, href: string): boolean {
  return pathname === href || (href !== '/dashboard' && pathname.startsWith(`${href}/`));
}

function getNavigationItemClassName(isActive: boolean): string {
  if (isActive) {
    return 'flex items-center px-3 py-2 mb-1 rounded-lg transition-colors bg-blue-50 text-blue-700';
  }

  return 'flex items-center px-3 py-2 mb-1 rounded-lg text-gray-700 transition-colors hover:bg-gray-100';
}

export function DashboardLayout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Probes', href: '/dashboard/monitors', icon: CheckCircle2 },
    { name: 'Jobs', href: '/dashboard/jobs', icon: BriefcaseBusiness },
    { name: 'Contacts', href: '/dashboard/contacts', icon: Users },
    { name: 'Connected clients', href: '/dashboard/connected-clients', icon: Plug },
    { name: 'Billing', href: '/dashboard/billing', icon: CreditCard },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b z-50">
        <div className="px-4 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <button
              type="button"
              className="text-gray-500 hover:text-gray-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            <span className="ml-3 text-lg font-semibold">Valvo.io</span>
          </div>
          <div className="text-sm text-gray-600">{user?.email}</div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-white pt-16">
          <nav id="mobile-navigation" aria-label="Mobile navigation" className="px-4 py-4">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = isNavigationItemActive(pathname, item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  aria-current={isActive ? 'page' : undefined}
                  className={getNavigationItemClassName(isActive)}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
            <Button onClick={handleSignOut} variant="outline" className="w-full">
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      )}

      <div className="flex h-screen">
        {/* Sidebar for desktop */}
        <div className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-white border-r">
          {/* Logo */}
          <div className="flex items-center h-16 px-6 border-b">
            <h1 className="text-xl font-bold text-gray-900">Valvo.io</h1>
          </div>

          {/* Navigation */}
          <nav aria-label="Primary navigation" className="flex-1 px-4 py-4">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = isNavigationItemActive(pathname, item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  aria-current={isActive ? 'page' : undefined}
                  className={getNavigationItemClassName(isActive)}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User info and logout */}
          <div className="p-4 border-t">
            <div className="mb-3">
              <p className="text-sm text-gray-500">Logged in as</p>
              <p className="text-sm font-medium text-gray-900 truncate">{user?.email}</p>
            </div>
            <Button onClick={handleSignOut} variant="outline" className="w-full">
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0 lg:pl-64">
          <main className="h-full">
            <div className="pt-16 lg:pt-0">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
